import { useMemo, useState } from 'react';
import { useGetProjectCoilsQuery } from 'app/services/projectBoardApi';
import {
  AhuSummaryPane,
  buildAhuSummaryRows,
  useRefetchOnTabReentry,
  type IAhuSummaryComponent,
} from './ahuSummaryShared';

/**
 * AHU COMPONENTS TAB (`#ahu_components`) — dev-sync F port of
 * `project/static/project/mvc/ahu_components/view/ahu_components.js` +
 * `tmpl/ahu_components.tmpl` + `collection/coils.js` (KA-5258, PR #798).
 *
 * Left rail lists the AHU component types that have a summary table (only
 * "Coils" today — ahu_components.js:23-28); the right pane shows that type's
 * READ-ONLY project-wide table, rows grouped by AHU with the AHU tag spanning
 * its group. Columns: AHU | TAG | TYPE | DUTY | HAND, fed by
 * `GET /api/v1.0/ahu/coils/project/{project_id}` → a flat array of
 * `{id, section_id, ahu, ahu_id, section, type, duty, hand}`
 * (`AHUCoilProjectSerializer`, api/rest_ahu/coil_serializers.py:300-337).
 * No inputs, no writes: editing is not gated here, it simply does not exist
 * (see ahuSummaryShared.tsx for the full read-only note).
 *
 * Wiring parity: the tab is a static mount in `container.tmpl:10` and the menu
 * entry is an ordinary DB row (`MasterProjectBoardTab` value `ahu_components`,
 * logo `list`), so BoardMenu/BoardTabs pick it up with no special-casing —
 * exactly the shape of dev's api.js:261-276 / dispatcher.js:30-31 wiring.
 */

/** ahu_components.js:26-28 — the sidebar registry. */
const COMPONENTS: readonly IAhuSummaryComponent[] = [{ key: 'coils', label: 'Coils' }];

/** ahu_components.tmpl:18-24. */
const HEAD = (
  <tr>
    <th className="ahu-summary__col-ahu">AHU</th>
    <th className="ahu-summary__col-name">TAG</th>
    <th className="ahu-summary__col-fixed-sm">TYPE</th>
    <th className="ahu-summary__col-fixed-sm">DUTY</th>
    <th className="ahu-summary__col-fixed-sm">HAND</th>
  </tr>
);

const AhuComponentsTab = ({
  projectId,
  active,
}: {
  projectId: number;
  active: boolean;
}) => {
  const { data: coils, refetch } = useGetProjectCoilsQuery(projectId, {
    skip: !Number.isFinite(projectId),
  });
  useRefetchOnTabReentry(active, refetch, 'ahu_components');

  // `selectedComponent` = the first registry entry (ahu_components.js:33).
  const [selected, setSelected] = useState<string>(COMPONENTS[0].key);

  const rows = useMemo(() => buildAhuSummaryRows(coils ?? []), [coils]);

  // Backbone renders only inside `fetch().then(...)` (ahu_components.js:44-49),
  // so the tab body is EMPTY until the first response lands — no spinner, no
  // "No Coil" flash. `undefined` data reproduces that; a refetch keeps the
  // previous rows on screen, same as re-rendering after the new fetch resolves.
  if (!coils) return null;

  return (
    <AhuSummaryPane
      formatId="ahu_components_format"
      sidebarHeader="AHU Components"
      components={COMPONENTS}
      selected={selected}
      onSelect={setSelected}
      head={HEAD}
      colSpan={5}
      emptyLabel="No Coil"
      isEmpty={coils.length === 0}
    >
      {rows.map(({ firstInGroup, ahu, rowspan, item }) => (
        <tr
          key={item.id}
          className={firstInGroup ? 'ahu-summary__group-start' : ''}
        >
          {firstInGroup && (
            <td className="ahu-summary__ahu" rowSpan={rowspan}>
              {ahu}
            </td>
          )}
          <td className="ahu-summary__col-name">{item.section}</td>
          <td className="ahu-summary__col-fixed-sm">{item.type}</td>
          <td className="ahu-summary__col-fixed-sm">{item.duty}</td>
          <td className="ahu-summary__col-fixed-sm">{item.hand}</td>
        </tr>
      ))}
    </AhuSummaryPane>
  );
};

export default AhuComponentsTab;
